import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GoogleMap, MarkerF, PolylineF, InfoWindowF, useJsApiLoader } from "@react-google-maps/api";
import backend from "../../../../../api";
import AppHeader from "../../../../../components/AppHeader";
import "./UserMap.css";
import { useSelector } from "react-redux";
import { getSocket, safeEmit } from "../../../../../api/socket";

export default function UserMap() {
  const { targetUserId } = useParams();
  const nav = useNavigate();
  const { user } = useSelector(s => s.auth);
  const userId = user?._id;

  const [loc, setLoc] = useState(null);
  const [err, setErr] = useState("");
  const [activeInfo, setActiveInfo] = useState(null);
  const [activePath, setActivePath] = useState(null);

  // ack + retry
  const reqAttemptsRef = useRef(0);
  const reqTimerRef = useRef(null);

  const mapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey:  "AIzaSyAOGi6Lrm0NrvFD6iuX7odfmhl_SM7qY8U",
  });

  const triggerMapResize = useCallback(() => {
    if (mapRef.current && window.google) {
      window.google.maps.event.trigger(mapRef.current, "resize");
    }
  }, []);
  useEffect(() => {
    window.addEventListener("resize", triggerMapResize);
    return () => window.removeEventListener("resize", triggerMapResize);
  }, [triggerMapResize]);

  const load = async () => {
    try {
      setErr("");
      if (!targetUserId) return;
      const res = await backend.get(`/location/${targetUserId}`);
      setLoc(res?.data?.data || null);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load location");
    }
  };

  useEffect(() => {
    load();
    const socket = getSocket();
    if (!socket) return;

    socket.emit("join-room", { roomId: `user:${targetUserId}` });

    const onUpdate = (data) => {
      if (String(data.userId) !== String(targetUserId)) return;
      setLoc(prev => {
        const newHist = prev?.history ? [...prev.history] : [];
        if (prev && (prev.lat !== data.lat || prev.lng !== data.lng)) {
          newHist.push({ lat: prev.lat, lng: prev.lng, accuracy: prev.accuracy, ts: prev.updatedAt });
        }
        return { ...data, history: newHist };
      });
    };

    const onDelivered = (data) => {
      // { to, delivered } — 'to' is the targetUserId we requested
      if (!data || String(data.to) !== String(targetUserId)) return;
      if (data.delivered) {
        clearTimeout(reqTimerRef.current); reqTimerRef.current = null; reqAttemptsRef.current = 0;
      } else {
        clearTimeout(reqTimerRef.current);
        reqAttemptsRef.current += 1;
        if (reqAttemptsRef.current > 8) { reqAttemptsRef.current = 0; return; }
        reqTimerRef.current = setTimeout(() => {
          safeEmit("location:request", { to: targetUserId, from: userId, fromName: user?.name }).catch(()=>{});
        }, 1200);
      }
    };

    socket.on("location:updated", onUpdate);
    socket.on("location:request-delivered", onDelivered);

    return () => {
      socket.off("location:updated", onUpdate);
      socket.off("location:request-delivered", onDelivered);
      socket.emit("leave-room", { roomId: `user:${targetUserId}` });
      if (reqTimerRef.current) { clearTimeout(reqTimerRef.current); reqTimerRef.current = null; }
    };
  }, [targetUserId, userId, user?.name]);

  const onMapLoad = useCallback((map) => { mapRef.current = map; triggerMapResize(); }, [triggerMapResize]);

  const center = useMemo(() => ({ lat: loc?.lat ?? 24.7136, lng: loc?.lng ?? 46.6753 }), [loc]);

  const fitToPath = (path) => {
    if (!mapRef.current || !window.google || !Array.isArray(path) || !path.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    path.forEach(p => bounds.extend(p));
    mapRef.current.fitBounds(bounds, { top: 60, right: 40, bottom: 40, left: 40 });
    triggerMapResize();
  };

  const onShowTrack = () => {
    if (!loc?.lat) return;
    const path = (loc.history || []).map(p => ({ lat: p.lat, lng: p.lng })).concat({ lat: loc.lat, lng: loc.lng });
    setActivePath(path);
    fitToPath(path);
    setActiveInfo({ id: targetUserId, name: "Target user", pos: { lat: loc.lat, lng: loc.lng } });
  };

  const onClearTrack = () => { setActivePath(null); setActiveInfo(null); };

  const requestLocation = async () => {
    reqAttemptsRef.current = 0;
    await safeEmit("location:request", { to: targetUserId, from: userId, fromName: user?.name });
  };

  return (
    <div className="um-page">
      <AppHeader
        title="User on map"
        onBack={() => nav(-1)}
        right={
          <div className="um-actions">
            <button className="um-btn" onClick={load}>Refresh</button>
            {!loc?.lat && <button className="um-btn" onClick={requestLocation}>Request location</button>}
            <button className="um-btn outline" onClick={onClearTrack}>Clear track</button>
          </div>
        }
      />

      {err && <div className="um-error">{err}</div>}
      {loadError && <div className="um-error">Map failed to load. Check API key.</div>}

      <div className="um-layout">
        <aside className="um-sidebar">
          <div className="um-card">
            <div className="um-subtitle">Tracked user</div>
            {loc?.lat ? (
              <div className="um-userRow" onClick={onShowTrack} role="button">
                <div className="um-dot brand" />
                <div className="um-userName">Target user</div>
                <div className="um-meta">Live location</div>
              </div>
            ) : (
              <div className="um-userRow">
                <div className="um-userName">No location available</div>
              </div>
            )}
          </div>

          <div className="um-card">
            <div className="um-subtitle">Legend</div>
            <div className="um-legendRow"><span className="um-line" /><span>Route history</span></div>
            <div className="um-legendRow"><span className="um-dot brand" /><span>User location</span></div>
          </div>
        </aside>

        <div className="um-mapWrap">
          {isLoaded ? (
            <GoogleMap
              zoom={13}
              center={center}
              onLoad={onMapLoad}
              mapContainerClassName="um-mapCanvas"
              options={{ disableDefaultUI: false, clickableIcons: false, mapTypeControl: false, streetViewControl: false }}
            >
              {loc?.lat && (
                <MarkerF position={{ lat: loc.lat, lng: loc.lng }} onClick={onShowTrack} label="T" title="Target user" />
              )}

              {activeInfo && (
                <InfoWindowF position={activeInfo.pos} onCloseClick={() => setActiveInfo(null)}>
                  <div style={{ minWidth: 140 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{activeInfo.name}</div>
                    <div style={{ color: "#818D93", fontSize: 12 }}>
                      {activeInfo.pos.lat.toFixed(5)}, {activeInfo.pos.lng.toFixed(5)}
                    </div>
                    <button className="um-btn small" style={{ marginTop: 8 }}
                      onClick={() => fitToPath(activePath)}>Zoom to track</button>
                  </div>
                </InfoWindowF>
              )}

              {activePath && (
                <PolylineF path={activePath} options={{ strokeColor: "#FCAA30", strokeOpacity: 0.95, strokeWeight: 4 }} />
              )}
            </GoogleMap>
          ) : (
            <div className="um-loading">Loading map…</div>
          )}
        </div>
      </div>
    </div>
  );
}
